// ASK-145 §11.9 — the after-clear hitch, attributed with LoAF before any fix.
//
// MEASURED: `coord-phone-load-profile` G5 on prod `c2b05a7`, 2 of 4 runs red with a 147–164 ms task
// within 0.1–1.6 s of loader-clear, LoAF invoker `setTimeout he@engine-CWUlP480.js:558659`. A local
// `--mode prod` rebuild of `c2b05a7` emits the byte-identical chunk name `engine-CWUlP480.js`; its
// source map resolves char 558659 to three.js `WebGLRenderer.compileAsync` → `checkMaterialsReady`,
// the 10 ms `setTimeout` poll that calls `program.isReady()` = `gl.getProgramParameter(…,
// COMPLETION_STATUS_KHR)`. In Chrome that is a SYNCHRONOUS round trip to the GPU process, which waits
// behind whatever the GPU is doing (shader links, texture uploads) — so a poll that runs after the
// loader has cleared lands its wait in the customer's first second.
//
// Both callers bounded their AWAIT with `Promise.race([compileAsync(…), timeout])` — but a race does
// not stop three's poller: it keeps firing every 10 ms until every program reports ready, however long
// that takes, after the shop is already on screen. Bailey's mount had no bound at all.
//
// So: the same work as `compileAsync` (`renderer.compile` → poll `isReady`), but the poll STOPS at its
// deadline (a program not ready by then compiles on first draw, which is what a timed-out race already
// promised), stops when its scene is superseded, and records what it did so the next profile can put
// each long task beside the compile that was polling at that moment.
export function compileBounded(renderer, object, camera, {
	targetScene = null, timeoutMs = 3000, intervalMs = 10, label = "compile",
	log = null, isCurrent = () => true,
	now = () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
	schedule = (fn, ms) => setTimeout(fn, ms),
} = {}) {
	const t0 = now();
	const pending = renderer.compile(object, camera, targetScene);   // Set<Material> — three's own compile
	const rec = { label, t0: Math.round(t0), programs: pending.size, polls: 0, outcome: null, t1: null };
	if (Array.isArray(log)) log.push(rec);
	return new Promise((resolve) => {
		const done = (outcome) => { rec.outcome = outcome; rec.t1 = Math.round(now()); resolve(rec); };
		const check = () => {
			if (!isCurrent()) return done("superseded");
			rec.polls++;
			for (const m of pending) {
				const program = renderer.properties.get(m)?.currentProgram;
				if (!program || program.isReady()) pending.delete(m);
			}
			if (pending.size === 0) return done("ready");
			if (now() - t0 >= timeoutMs) return done("timeout");   // STOP — never a background poller
			schedule(check, intervalMs);
		};
		check();
	});
}
